import { useState, useEffect, useCallback } from "react";
import { AlbumLink, CreateAlbumLinkDto } from "../types/album";
import { albumService } from "../services/albumService";

interface UseAlbumLinksResult {
  links: AlbumLink[];
  isLoading: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  error: string | null;
  createLink: (permission: "read_only" | "upload") => Promise<AlbumLink | null>;
  deleteLink: (linkId: string) => Promise<boolean>;
  refreshLinks: () => Promise<void>;
  clearError: () => void;
}

export function useAlbumLinks(
  ownerId: string | undefined,
  albumId: string | undefined
): UseAlbumLinksResult {
  const [links, setLinks] = useState<AlbumLink[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLinks = useCallback(async () => {
    if (!ownerId || !albumId) return;

    setIsLoading(true);
    setError(null);

    try {
      const fetchedLinks = await albumService.getAlbumLinks(ownerId, albumId);
      setLinks(fetchedLinks);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load album links";
      setError(message);
      setLinks([]);
    } finally {
      setIsLoading(false);
    }
  }, [ownerId, albumId]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const createLink = useCallback(
    async (permission: "read_only" | "upload"): Promise<AlbumLink | null> => {
      if (!ownerId || !albumId) return null;

      setIsCreating(true);
      setError(null);

      try {
        const linkData: CreateAlbumLinkDto = { permission };
        const newLink = await albumService.createAlbumLink(ownerId, albumId, linkData);
        setLinks((prev) => [...prev, newLink]);
        return newLink;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to create link";
        setError(message);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [ownerId, albumId]
  );

  const deleteLink = useCallback(
    async (linkId: string): Promise<boolean> => {
      if (!ownerId || !albumId) return false;

      setIsDeleting(true);
      setError(null);

      try {
        await albumService.deactivateAlbumLink(ownerId, albumId, linkId);
        setLinks((prev) => prev.filter((link) => link.id !== linkId));
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to delete link";
        setError(message);
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [ownerId, albumId]
  );

  const refreshLinks = useCallback(async () => {
    await fetchLinks();
  }, [fetchLinks]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    links,
    isLoading,
    isCreating,
    isDeleting,
    error,
    createLink,
    deleteLink,
    refreshLinks,
    clearError,
  };
}
